import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
(pdfMake as any).vfs = (pdfFonts as any).pdfMake?.vfs || (pdfFonts as any).vfs;
import { Logo } from '../../common/logo-base64';
import { Utils } from '../../common/utils';
import { NonConformityActionSuggestion } from '../../cloud/services/sog-suggest.service';

const PRIORITY_ORDER: Record<string, number> = { High: 0, Medium: 1, Low: 2 };

export interface ActionSuggestionRow extends NonConformityActionSuggestion {
  selected: boolean;
}

@Component({
    selector: 'ai-action-suggestion-dialog',
    templateUrl: './ai-action-suggestion-dialog.component.html',
    styleUrl: './ai-action-suggestion-dialog.component.css',
    standalone: false
})
export class AiActionSuggestionDialogComponent {
  title: string;
  rows: ActionSuggestionRow[];
  downloading: boolean = false;
  generating: boolean = false;
  onGenerateJobs?: (actions: NonConformityActionSuggestion[]) => void | Promise<void>;

  private readonly actionTypeLabels = new Map<string, string>([
    ['ReSample', 'Re-Sample'],
    ...Utils.workListTypesOptions.map(o => [o.value, o.viewValue] as [string, string])
  ]);

  constructor(public dialogRef: MatDialogRef<AiActionSuggestionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AiActionSuggestionDialogModel) {
    this.title = data.title;
    this.onGenerateJobs = data.onGenerateJobs;
    this.rows = [...(data.actions ?? [])]
      .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1))
      .map(action => ({ ...action, selected: true }));
  }

  get hasSelectedRows(): boolean {
    return this.rows.some(row => row.selected);
  }

  get allSelected(): boolean {
    return this.rows.length > 0 && this.rows.every(row => row.selected);
  }

  toggleSelectAll(checked: boolean): void {
    this.rows.forEach(row => row.selected = checked);
  }

  actionTypeLabel(actionType: string): string {
    return this.actionTypeLabels.get(actionType) || actionType;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  async generateJobs(): Promise<void> {
    const selectedActions = this.rows.filter(row => row.selected);
    if (!this.onGenerateJobs || selectedActions.length === 0) {
      return;
    }

    this.generating = true;
    try {
      await this.onGenerateJobs(selectedActions);
    } finally {
      this.generating = false;
    }
  }

  downloadAsPDF(): void {
    this.downloading = true;
    try {
      const tableBody: any[][] = [
        ['Priority', 'Outlet', 'Location', 'Suggested Action', 'Reason', 'NC References'].map(text => ({ text, style: 'tableHeader' }))
      ];

      for (const action of this.rows) {
        tableBody.push([
          action.priority,
          String(action.outletNumber),
          action.location,
          this.actionTypeLabel(action.actionType),
          action.reason,
          action.ncReferences.join(', ')
        ]);
      }

      const pdfContent: any[] = [
        { image: Logo.proEconomyLogoBase64Url, width: 120, margin: [0, 0, 0, 12] },
        { text: this.title, style: 'dialogTitle', margin: [0, 0, 0, 16], alignment: 'center' },
        this.rows.length === 0
          ? { text: 'No actions were suggested for the analysed non-conformities.' }
          : {
              table: {
                headerRows: 1,
                widths: ['auto', 'auto', 'auto', 'auto', '*', 'auto'],
                body: tableBody
              },
              layout: 'lightHorizontalLines',
              fontSize: 9
            }
      ];

      const docDefinition: any = {
        pageOrientation: 'landscape',
        content: pdfContent,
        defaultStyle: { fontSize: 9 },
        styles: {
          dialogTitle: {
            fontSize: 16,
            bold: true
          },
          tableHeader: {
            bold: true,
            fillColor: '#f5f5f5'
          }
        }
      };
      const safeTitle = this.title.replace(/[^a-z0-9 _-]/gi, '_');
      pdfMake.createPdf(docDefinition).download(`${safeTitle}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      this.downloading = false;
    }
  }
}


export class AiActionSuggestionDialogModel {
  constructor(
    public title: string,
    public actions: NonConformityActionSuggestion[],
    public onGenerateJobs?: (actions: NonConformityActionSuggestion[]) => void | Promise<void>
  ) {}
}
